import { observable, action, computed, toJS, reaction } from "mobx";
import toastService from "../services/ToastService";
import moment from "moment";
import {
  noBranchSelectedBranch,
  cm_CONSTANT_entity_tables,
  EntityTypesThatCanBeOrphan,
} from "./../constants/constants-container";

import { FilterType } from "../constants/FilterType";
import {
  StringOperator,
  NumberOperator,
  DateOperator,
  BooleanOperator,
  EnumOperator,
} from "../constants/FilterOperators";

import validationService from "src/services/ValidationService";

const defaultLimit = 50;
const defaultOffset = 0;

const defaultMenuGroupStates = {
  "": "open",
  Core: "open",
  "Data Definition": "open",
  Mapping: "open",
  Rendering: "open",
  Transformation: "open",
  "Legal Reference": "open",
};

const tableMapLocalStorageKey = "cm-table-map";
const filterPanelMapLocalStorageKey = "cm-filter-panel-map";
const nextFilterIdLocalStorageKey = "cm-next-filter-id";
const menuGroupStatesLocalStorageKey = "cm-menu-group-states";
const showProductionDataOnlyGloballyLocalStorageKey =
  "cm-showProductionDataOnlyGlobally";
const isGlobalProductionDataFilterAddedLocalStorageKey =
  "cm-isGlobalProductionDataFilterAdded";
const showOrphansOnlyGloballyLocalStorageKey = "cm-showOrphansOnlyGlobally";
const isGlobalOrphanFilterAddedLocalStorageKey = "cm-isGlobalOrphanFilterAdded";
const showWorkingDataOnlyGloballyLocalStorageKey =
  "cm-showWorkingDataOnlyGlobally";
const isGlobalWorkingFilterAddedLocalStorageKey =
  "cm-isGlobalWorkingDataFilterAdded";

const IS_BIRD_APP = process.env.REACT_APP_ENV === "bird";

export default class ContentManagementStore {
  debounce = (fn) => {
    let timeout;
    return function () {
      const functionCall = () => fn.apply(this, arguments);
      clearTimeout(timeout);
      timeout = setTimeout(functionCall, 900);
    };
  };

  constructor(http, exporter, importer, userStore) {
    this.http = http;
    this.exporter = exporter;
    this.importer = importer;
    this.userStore = userStore;
    this.tableMap = this.initTableMap();
    this.filterPanelMap = this.initFilterPanelMap();
    this.menuGroupStates = this.initMenuGroupStates();
    this.fetchAllBranchesData();
    this.init();

    // Turned off processing of URL search params, since we introduced HashRouter and it does not work anymore
    // this.processURLSearchParams();

    reaction(
      () => this.globalFilters,
      (filters) => {
        this.filterPanelMap = this.initFilterPanelMap();
        this.tableMap = this.initTableMap();
      }
    );
    reaction(
      () => this.userStore.obj_userData.roles,
      (roles) => {
        if (!IS_BIRD_APP) {
          if (
            this.userStore.isUserDaOrPoOrMao ||
            this.userStore.isUserSystemAdministrator
          ) {
            this.globalFilters = localStorage.getItem("cm-global-filters")
              ? JSON.parse(localStorage.getItem("cm-global-filters"))
              : {
                  production: {
                    label: "Production data",
                    enabled: true,
                  },
                  working: {
                    label: "Working branch",
                    enabled: true,
                  },
                  master: {
                    label: "Master branch",
                    enabled: true,
                  },
                };
          } else {
            this.globalFilters = {
              production: {
                label: "Production data",
                enabled: false,
              },
              working: {
                label: "Working branch",
                enabled: false,
              },
              master: {
                label: "Master branch",
                enabled: false,
              },
            };
          }
        }
      }
    );
  }

  @observable nextFilterId =
    localStorage.getItem(nextFilterIdLocalStorageKey) || "0";

  @observable allEntitiesArray = [];

  @observable
  selectedBranchObject = {
    bid: null,
    masterBid: null,
    branchName: "",
    projectName: "",
    branchType: "",
  };

  @observable urlSearchParams = observable.map();

  @observable menuGroupStates = defaultMenuGroupStates;

  @observable
  globalFiltersPanelOpen = true;

  @observable
  showProductionDataOnlyGlobally = localStorage.getItem(
    showProductionDataOnlyGloballyLocalStorageKey
  )
    ? JSON.parse(
        localStorage.getItem(showProductionDataOnlyGloballyLocalStorageKey)
      )
    : true;

  @observable
  showOrphansOnlyGlobally = localStorage.getItem(
    showOrphansOnlyGloballyLocalStorageKey
  )
    ? JSON.parse(localStorage.getItem(showOrphansOnlyGloballyLocalStorageKey))
    : false;

  @observable
  showWorkingDataOnlyGlobally = localStorage.getItem(
    showWorkingDataOnlyGloballyLocalStorageKey
  )
    ? JSON.parse(
        localStorage.getItem(showWorkingDataOnlyGloballyLocalStorageKey)
      )
    : false;

  @observable
  isGlobalProductionDataFilterAdded = localStorage.getItem(
    isGlobalProductionDataFilterAddedLocalStorageKey
  )
    ? JSON.parse(
        localStorage.getItem(isGlobalProductionDataFilterAddedLocalStorageKey)
      )
    : false;

  @observable
  isGlobalOrphanFilterAdded = localStorage.getItem(
    isGlobalOrphanFilterAddedLocalStorageKey
  )
    ? JSON.parse(localStorage.getItem(isGlobalOrphanFilterAddedLocalStorageKey))
    : false;

  @observable
  isGlobalWorkingDataFilterAdded = localStorage.getItem(
    isGlobalWorkingFilterAddedLocalStorageKey
  )
    ? JSON.parse(
        localStorage.getItem(isGlobalWorkingFilterAddedLocalStorageKey)
      )
    : false;

  @observable
  globalFilters = {};

  @observable
  relatedEntities = [];

  @observable
  affectedEntities = [];

  @observable
  relatedEntityTypeCount = {};

  @observable
  affectedEntityTypeCount = {};

  @observable
  allCurrentbranches = [];

  @observable
  ob_fetched_po_data = {
    maintenanceAgencies: [],
  };

  @observable
  ob_fetched_ma_data = {
    maintenanceAgencies: [],
  };

  @observable
  timer = undefined;

  @observable
  quickfiltersValues = {};

  @action
  init = () => {
    this.helper_fetch_po_data();
    this.helper_fetch_all_projects();
  };

  @action
  helper_fetch_po_data = () => {
    if (IS_BIRD_APP) return;

    this.http.service_fetch_po_data().then((data) => {
      try {
        this.ob_fetched_po_data = {
          maintenanceAgencies: data.maintenanceAgencies,
        };
      } catch (error) {
        console.log("ob_fetched_po_data", error.message);
      }
    });
  };

  @action
  helper_fetch_all_projects = () => {
    if (IS_BIRD_APP) return;

    this.http.service_fetch_ALLMA().then(
      action((obj_mao_dataModel) => {
        try {
          this.ob_fetched_ma_data = {
            maintenanceAgencies: obj_mao_dataModel.maintenanceAgencies,
          };
        } catch (error) {
          console.error(error.message);
          this.ob_fetched_ma_data.maintenanceAgencies = [];
        }
      })
    );
  };

  checkIfMissing = () => {
    const tableMapFromLocalStorage = localStorage.getItem(
      tableMapLocalStorageKey
    );
    if (!tableMapFromLocalStorage) return;
    const parsedTableMapAsObject = JSON.parse(tableMapFromLocalStorage);
    const expectedTableKeys = cm_CONSTANT_entity_tables
      .map((tableGroup) => tableGroup.items)
      .reduce((a, b) => a.concat(b))
      .map((table) => table.value);
    const storedTableKeys = Object.keys(parsedTableMapAsObject);
    const missing = expectedTableKeys.filter(
      (x) => !storedTableKeys.includes(x)
    );
    if (missing && missing.length > 0) {
      const tableMap = observable.map(this.createTableMap());
      localStorage.setItem(tableMapLocalStorageKey, JSON.stringify(tableMap));
      const filterPanelMap = observable.map(this.createFilterPanelMap());
      localStorage.setItem(
        filterPanelMapLocalStorageKey,
        JSON.stringify(filterPanelMap)
      );
    }
  };

  initTableMap = () => {
    this.checkIfMissing();
    const tableMapFromLocalStorage = localStorage.getItem(
      tableMapLocalStorageKey
    );
    if (tableMapFromLocalStorage) {
      const parsedTableMapAsObject = JSON.parse(tableMapFromLocalStorage);
      const parsedTableMap = new Map();
      Object.keys(parsedTableMapAsObject).forEach((key) => {
        // temporary logic handling renaming of VariableSetMappingItem to VariableSetMapping
        if (key === "VariableSetMappingItem") {
          parsedTableMap.set("VariableSetMapping", {
            isOpen: parsedTableMapAsObject[key].isOpen,
            minRows: parsedTableMapAsObject[key].minRows,
            isColumnSelectorOpen:
              parsedTableMapAsObject[key].isColumnSelectorOpen,
            selectedColumns: parsedTableMapAsObject[key].selectedColumns,
            selectedRows: observable.map(),
            areAllRowsSelected: false,
            isEveryFilteredEntitySelected: false,
            sorted: parsedTableMapAsObject[key].sorted,
            limit: parsedTableMapAsObject[key].limit,
            data: parsedTableMapAsObject[key].data,
          });
        } else {
          parsedTableMap.set(key, {
            isOpen: parsedTableMapAsObject[key].isOpen,
            minRows: parsedTableMapAsObject[key].minRows,
            isColumnSelectorOpen:
              parsedTableMapAsObject[key].isColumnSelectorOpen,
            selectedColumns: parsedTableMapAsObject[key].selectedColumns,
            selectedRows: observable.map(),
            areAllRowsSelected: false,
            isEveryFilteredEntitySelected: false,
            sorted: parsedTableMapAsObject[key].sorted,
            limit: parsedTableMapAsObject[key].limit,
            data: parsedTableMapAsObject[key].data,
          });
        }
      });
      return observable.map(parsedTableMap);
    } else {
      const tableMap = observable.map(this.createTableMap());
      localStorage.setItem(tableMapLocalStorageKey, JSON.stringify(tableMap));
      return tableMap;
    }
  };

  parseSelectedRows = (parsedTableMapAsObject, key) => {
    const selectedRowsMap = new Map();
    Object.keys(parsedTableMapAsObject[key].selectedRows).forEach((k) => {
      selectedRowsMap.set(k, parsedTableMapAsObject[key].selectedRows[k]);
    });
    return selectedRowsMap;
  };

  createTableMap = () => {
    const tableMap = new Map();
    cm_CONSTANT_entity_tables.forEach((tableGroup, tableGroupIndex) => {
      tableGroup.items.forEach((table, tableIndex) => {
        tableMap.set(table.value, {
          isOpen: false,
          minRows: 1,
          isColumnSelectorOpen: false,
          selectedColumns: [],
          selectedRows: observable.map(),
          areAllRowsSelected: false,
          isEveryFilteredEntitySelected: false,
          sorted: [],
          limit: {
            limit: tableGroup.category !== "" ? defaultLimit : 5,
            offset: defaultOffset,
          },
          data: { metadata: {}, results: [], totalResults: -1 },
        });
      });
    });
    return tableMap;
  };

  initFilterPanelMap = () => {
    this.checkIfMissing();
    const filterPanelMapFromLocalStorage = localStorage.getItem(
      filterPanelMapLocalStorageKey
    );
    if (filterPanelMapFromLocalStorage) {
      const parsedFilterPanelMapAsObject = JSON.parse(
        filterPanelMapFromLocalStorage
      );
      const parsedFilterPanelMap = new Map();
      Object.keys(parsedFilterPanelMapAsObject).forEach((key) => {
        if (key === "VariableSetMappingItem") {
          // temporary logic handling renaming of VariableSetMappingItem to VariableSetMapping
          parsedFilterPanelMap.set("VariableSetMapping", {
            isOpen: parsedFilterPanelMapAsObject[key].isOpen,
            filters: observable.map(
              Object.entries(parsedFilterPanelMapAsObject[key].filters)
            ),
            globalFiltersForTable:
              parsedFilterPanelMapAsObject[key].globalFiltersForTable,
          });
          // store updated map to local storage
          localStorage.setItem(
            filterPanelMapLocalStorageKey,
            JSON.stringify(parsedFilterPanelMap)
          );
        } else {
          // causes some issues - not recognized properly (maybe not initialized before calling?)
          // remove global filters if user is not DA/PO/MAO/SA
          // if ( !this.userStore.isUserDaOrPoOrMao && !this.userStore.isUserSystemAdministrator ) {
          //   const idsToBeRemoved1 = Object.values(parsedFilterPanelMapAsObject[key].filters).filter((f) => f.isGlobal === true).map(f => f.id);
          //   if(idsToBeRemoved1.length > 0){
          //     idsToBeRemoved1.forEach(id => delete parsedFilterPanelMapAsObject[key].filters[id]);
          //   }
          //   // set all global content filters to false
          //   if(parsedFilterPanelMapAsObject[key].globalFiltersForTable) {
          //     Object.keys(parsedFilterPanelMapAsObject[key].globalFiltersForTable).forEach((k) => parsedFilterPanelMapAsObject[key].globalFiltersForTable[k] = false);
          //   }
          //   // reset global orphan filter flag
          //   localStorage.setItem(isGlobalOrphanFilterAddedLocalStorageKey, false);
          // }
          // remove old global typeOfBranch filters if present
          const idsToBeRemoved2 = Object.values(
            parsedFilterPanelMapAsObject[key].filters
          )
            .filter((f) => f.isGlobal === true && f.type === "BRANCHTYPE")
            .map((f) => f.id);
          if (idsToBeRemoved2.length > 0) {
            idsToBeRemoved2.forEach(
              (id) => delete parsedFilterPanelMapAsObject[key].filters[id]
            );
          }
          // process filters
          parsedFilterPanelMap.set(key, {
            isOpen: parsedFilterPanelMapAsObject[key].isOpen,
            filters: observable.map(
              Object.entries(parsedFilterPanelMapAsObject[key].filters)
            ),
            globalFiltersForTable:
              !IS_BIRD_APP &&
              (this.userStore.isUserDaOrPoOrMao ||
                this.userStore.isUserSystemAdministrator)
                ? parsedFilterPanelMapAsObject[key].globalFiltersForTable
                : {},
          });
        }
      });
      return observable.map(parsedFilterPanelMap);
    } else {
      const filterPanelMap = observable.map(this.createFilterPanelMap());
      localStorage.setItem(
        filterPanelMapLocalStorageKey,
        JSON.stringify(filterPanelMap)
      );
      return filterPanelMap;
    }
  };

  createFilterPanelMap = () => {
    const filterPanelMap = new Map();
    cm_CONSTANT_entity_tables.forEach((tableGroup, tableGroupIndex) => {
      tableGroup.items.forEach((table, tableIndex) => {
        filterPanelMap.set(table.value, {
          isOpen: false,
          latestFilterChangesAreApplied: true,
          filters: observable.map(),
          globalFiltersForTable: this.createGlobalFiltersForTable(),
        });
      });
    });
    return filterPanelMap;
  };

  createGlobalFiltersForTable = () => {
    const obj = {};
    Object.keys(this.globalFilters).forEach((globalFilterKey) => {
      obj[globalFilterKey] = this.globalFilters[globalFilterKey].enabled;
    });
    return obj;
  };

  /*
  @action
  addGlobalProductionBranchFilter = () => {
    cm_CONSTANT_entity_tables.forEach((tableGroup, tableGroupIndex) => {
      tableGroup.items.forEach((table, tableIndex) => {
        if (
          !table.label.includes("MAINTENANCE_AGENCY") &&
          !table.label.includes("FRAMEWORK") &&
          !table.label.includes("prefiltered")
        ) {
          const globalProductionBranchFilter = {
            id: this.nextFilterId,
            column: "typeOfBranch",
            type: "BRANCHTYPE",
            operator: EnumOperator.IN.value,
            value: ["PRODUCTION"],
            columnOptionsWithType: [
              {
                name: "typeOfBranch",
                label: "Branch Type",
                type: "BRANCHTYPE",
              },
            ],
            operatorOptions: this.getOperatorOptionsForType("BRANCHTYPE"),
            enabled: true,
            isGlobal: true,
          };
          this.filterPanelMap
            .get(table.value)
            .filters.set(this.nextFilterId, globalProductionBranchFilter);
          localStorage.setItem(
            filterPanelMapLocalStorageKey,
            JSON.stringify(this.filterPanelMap)
          );
          this.nextFilterId = (parseInt(this.nextFilterId, 10) + 1).toString();
          localStorage.setItem(nextFilterIdLocalStorageKey, this.nextFilterId);
          this.isGlobalProductionDataFilterAdded = true;
          localStorage.setItem(
            isGlobalProductionDataFilterAddedLocalStorageKey,
            this.isGlobalProductionDataFilterAdded
          );
        }
      });
    });
  };
  */

  @action
  addGlobalOrphanFilter = () => {
    EntityTypesThatCanBeOrphan.forEach((type) => {
      const globalOrphanFilter = {
        id: this.nextFilterId,
        column: "showOrphansOnly",
        type: "BOOLEAN",
        operator: BooleanOperator.IS_EMPTY.value,
        value: true,
        columnOptionsWithType: [
          {
            name: "showOrphansOnly",
            label: "Orphans Only",
            type: "BOOLEAN",
          },
        ],
        operatorOptions: this.getOperatorOptionsForType("BOOLEAN"),
        enabled: false,
        isGlobal: true,
      };
      this.filterPanelMap
        .get(type)
        .filters.set(this.nextFilterId, globalOrphanFilter);
      localStorage.setItem(
        filterPanelMapLocalStorageKey,
        JSON.stringify(this.filterPanelMap)
      );
      this.nextFilterId = (parseInt(this.nextFilterId, 10) + 1).toString();
      localStorage.setItem(nextFilterIdLocalStorageKey, this.nextFilterId);
      this.isGlobalOrphanFilterAdded = true;
      localStorage.setItem(
        isGlobalOrphanFilterAddedLocalStorageKey,
        this.isGlobalOrphanFilterAdded
      );
    });
  };

  /*
  @action
  addGlobalWorkingBranchFilter = () => {
    cm_CONSTANT_entity_tables.forEach((type) => {
      const globalWorkingFilter = {
        id: this.nextFilterId,
        column: "showWorkingBranch",
        type: "BOOLEAN",
        operator: BooleanOperator.IS_EMPTY.value,
        value: true,
        columnOptionsWithType: [
          {
            name: "showWorkingBranch",
            label: "Working branch",
            type: "BOOLEAN",
          },
        ],
        operatorOptions: this.getOperatorOptionsForType("BOOLEAN"),
        enabled: false,
        isGlobal: true,
      };
      this.filterPanelMap
        .get(type)
        .filters.set(this.nextFilterId, globalWorkingFilter);
      localStorage.setItem(
        filterPanelMapLocalStorageKey,
        JSON.stringify(this.filterPanelMap)
      );
      this.nextFilterId = (parseInt(this.nextFilterId, 10) + 1).toString();
      localStorage.setItem(nextFilterIdLocalStorageKey, this.nextFilterId);
      this.isGlobalWorkingFilterAdded = true;
      localStorage.setItem(
        isGlobalWorkingFilterAddedLocalStorageKey,
        this.isGlobalWorkingDataFilterAdded
      );
    });
  };
  */

  @action
  initMenuGroupStates = () => {
    const menuGroupStatesFromLocalStorage = localStorage.getItem(
      menuGroupStatesLocalStorageKey
    );
    if (menuGroupStatesFromLocalStorage) {
      return JSON.parse(menuGroupStatesFromLocalStorage);
    } else {
      return defaultMenuGroupStates;
    }
  };

  @action
  toggleMenuGroup = (group) => {
    if (group && Object.keys(defaultMenuGroupStates).includes(group)) {
      this.menuGroupStates[group] =
        this.menuGroupStates[group] === "open" ? "closed" : "open";
      localStorage.setItem(
        menuGroupStatesLocalStorageKey,
        JSON.stringify(this.menuGroupStates)
      );
    }
  };

  @action
  resetCurrentPage = (tableName) => {
    this.tableMap.get(tableName).limit.offset = defaultOffset;
    localStorage.setItem(
      tableMapLocalStorageKey,
      JSON.stringify(this.tableMap)
    );
  };

  @action
  resetSelectedRows = (tableName) => {
    this.tableMap.get(tableName).selectedRows.clear();
    localStorage.setItem(
      tableMapLocalStorageKey,
      JSON.stringify(this.tableMap)
    );
  };

  @action
  fetchContentManagementTableDataWithCallback = (tableName) => {
    this.resetCurrentPage(tableName);
    this.fetchContentManagementTableData(tableName, () =>
      this.updateMinRowsForTable(tableName)
    );
  };

  @action
  toggleFilterPanelForTable = (tableName) => {
    let filterPanelData = this.filterPanelMap.get(tableName);
    filterPanelData.isOpen = !filterPanelData.isOpen;
    this.filterPanelMap.set(tableName, filterPanelData);
    localStorage.setItem(
      filterPanelMapLocalStorageKey,
      JSON.stringify(this.filterPanelMap)
    );
  };

  @action
  toggleGlobalFiltersPanel = () => {
    this.globalFiltersPanelOpen = !this.globalFiltersPanelOpen;
  };

  /*
  @action
  toggleShowProductionDataOnlyGlobalBranchTypeFilter = () => {
    this.showProductionDataOnlyGlobally = !this.showProductionDataOnlyGlobally;
    localStorage.setItem(
      showProductionDataOnlyGloballyLocalStorageKey,
      this.showProductionDataOnlyGlobally
    );
    cm_CONSTANT_entity_tables.forEach((tableGroup, tableGroupIndex) => {
      tableGroup.items.forEach((table, tableIndex) => {
        if (
          !table.label.includes("MAINTENANCE_AGENCY") &&
          !table.label.includes("FRAMEWORK") &&
          !table.label.includes("prefiltered")
        ) {
          const filtersForTable = this.filterPanelMap.get(table.value).filters;
          filtersForTable.forEach((value, key) => {
            if (value.isGlobal === true && value.column === "typeOfBranch") {
              const updatedFilter = {
                ...value,
                enabled:
                  this.showProductionDataOnlyGlobally === value.enabled
                    ? this.showProductionDataOnlyGlobally
                    : value.enabled === false &&
                      this.showProductionDataOnlyGlobally === true
                    ? true
                    : false,
              };
              filtersForTable.set(key, updatedFilter);
            }
          });
          this.filterPanelMap.get(table.value).filters = filtersForTable;
        }
      });
    });
    localStorage.setItem(
      filterPanelMapLocalStorageKey,
      JSON.stringify(this.filterPanelMap)
    );
    this.getOpenedTables.forEach((table) =>
      this.fetchContentManagementTableData(table, null, false)
    );
  };
*/

  @action
  toggleShowOrphansOnlyGloballyFilter = () => {
    this.showOrphansOnlyGlobally = !this.showOrphansOnlyGlobally;
    localStorage.setItem(
      showOrphansOnlyGloballyLocalStorageKey,
      this.showOrphansOnlyGlobally
    );
    EntityTypesThatCanBeOrphan.forEach((type) => {
      const filtersForTable = this.filterPanelMap.get(type).filters;
      filtersForTable.forEach((filter, key) => {
        if (filter.isGlobal === true && filter.column === "showOrphansOnly") {
          const updatedOrphanFilter = {
            ...filter,
            enabled:
              this.showOrphansOnlyGlobally === filter.enabled
                ? this.showOrphansOnlyGlobally
                : filter.enabled === false &&
                  this.showOrphansOnlyGlobally === true
                ? true
                : false,
          };
          filtersForTable.set(key, updatedOrphanFilter);
        }
      });
      this.filterPanelMap.get(type).filters = filtersForTable;
    });
    localStorage.setItem(
      filterPanelMapLocalStorageKey,
      JSON.stringify(this.filterPanelMap)
    );
    this.getOpenedTables.forEach((table) =>
      this.fetchContentManagementTableData(table, null, false)
    );
  };

  /*
  @action
  toggleShowWorkingDataOnlyGlobalBranchTypeFilter = () => {
    this.showWorkingDataOnlyGlobally = !this.showWorkingDataOnlyGlobally;
    localStorage.setItem(
      showWorkingDataOnlyGloballyLocalStorageKey,
      this.showWorkingDataOnlyGlobally
    );
    cm_CONSTANT_entity_tables.forEach((tableGroup, tableGroupIndex) => {
      tableGroup.items.forEach((table, tableIndex) => {
        if (
          !table.label.includes("MAINTENANCE_AGENCY") &&
          !table.label.includes("FRAMEWORK") &&
          !table.label.includes("prefiltered")
        ) {
          const filtersForTable = this.filterPanelMap.get(table.value).filters;
          filtersForTable.forEach((value, key) => {
            if (value.isGlobal === true && value.column === "bid") {
              const updatedFilter = {
                ...value,
                enabled:
                  this.showWorkingDataOnlyGlobally === value.enabled
                    ? this.showWorkingDataOnlyGlobally
                    : value.enabled === false &&
                      this.showWorkingDataOnlyGlobally === true
                    ? true
                    : false,
              };
              filtersForTable.set(key, updatedFilter);
            }
          });
          this.filterPanelMap.get(table.value).filters = filtersForTable;
        }
      });
    });
    localStorage.setItem(
      filterPanelMapLocalStorageKey,
      JSON.stringify(this.filterPanelMap)
    );
    this.getOpenedTables.forEach((table) =>
      this.fetchContentManagementTableData(table, null, false)
    );
  };
  */

  @action
  removeFiltersForTable = (tableName) => {
    const filtersToClear = this.filterPanelMap.get(tableName).filters;
    let deletedFilters = 0;
    if (filtersToClear.size > 0) {
      filtersToClear.forEach((value, key) => {
        if (value.isGlobal === false) {
          filtersToClear.delete(key);
          deletedFilters++;
        }
      });
      if (deletedFilters > 0) {
        this.fetchContentManagementTableData(tableName, () =>
          this.updateMinRowsForTable(tableName)
        );
        localStorage.setItem(
          filterPanelMapLocalStorageKey,
          JSON.stringify(this.filterPanelMap)
        );
        this.resetCurrentPage(tableName);
      }
    }
  };

  @action
  addOrUpdateQuickFilter = (tableName, column, value) => {
    const type = this.getTypeOfColumn(tableName, column);
    const operator = this.getQuickFilterOperatorForType(type);
    const existingFilter = Object.values(
      this.getFiltersByTableName(tableName)
    ).find(
      (f) =>
        f.column === column &&
        f.operator === operator.value &&
        f.isGlobal === false
    );
    if (existingFilter) {
      if (existingFilter.value !== value) {
        existingFilter.value = value;
      }
      if (existingFilter.enabled === false) {
        existingFilter.enabled = true;
      }
      this.filterPanelMap
        .get(tableName)
        .filters.set(existingFilter.id, existingFilter);
      localStorage.setItem(
        filterPanelMapLocalStorageKey,
        JSON.stringify(this.filterPanelMap)
      );
      this.nextFilterId = (parseInt(this.nextFilterId, 10) + 1).toString();
      localStorage.setItem(nextFilterIdLocalStorageKey, this.nextFilterId);
      this.resetCurrentPage(tableName);
      if (existingFilter.enabled) {
        this.fetchContentManagementTableData(tableName, () =>
          this.updateMinRowsForTable(tableName)
        );
      }
    } else {
      const columnOptionsWithType = this.getColumns(tableName);
      const operator = this.getQuickFilterOperatorForType(type).value;
      const filter = {
        id: this.nextFilterId,
        column: column,
        type: type,
        operator: operator,
        value: value,
        columnOptionsWithType: columnOptionsWithType,
        operatorOptions: this.getOperatorOptionsForType(type),
        enabled: !this.isOperatorAlreadyAppliedForColumn(
          tableName,
          column,
          operator
        ),
        isGlobal: false,
      };
      this.filterPanelMap.get(tableName).filters.set(this.nextFilterId, filter);
      localStorage.setItem(
        filterPanelMapLocalStorageKey,
        JSON.stringify(this.filterPanelMap)
      );
      this.nextFilterId = (parseInt(this.nextFilterId, 10) + 1).toString();
      localStorage.setItem(nextFilterIdLocalStorageKey, this.nextFilterId);
      this.resetCurrentPage(tableName);
      if (filter.enabled) {
        this.fetchContentManagementTableData(tableName, () =>
          this.updateMinRowsForTable(tableName)
        );
      }
    }
  };

  @action
  addOrUpdateQuickFilterDebounce = (tableName, column, value) => {
    this.quickfiltersValues[`${tableName + column}`] = value;
    clearTimeout(this.timer);
    this.timer = setTimeout(() => {
      this.addOrUpdateQuickFilter(tableName, column, value);
    }, 1500);
  };

  @action
  addFilter = (tableName, columnOptionsWithType) => {
    const defaultOperator = this.getDefaultOperatorForType(
      columnOptionsWithType[0].type
    ).value;
    const filter = {
      id: this.nextFilterId,
      column: columnOptionsWithType[0].name,
      type: columnOptionsWithType[0].type,
      operator: defaultOperator,
      value: this.getDefaultValueForType(columnOptionsWithType[0].type),
      columnOptionsWithType: columnOptionsWithType,
      operatorOptions: this.getOperatorOptionsForType(
        columnOptionsWithType[0].type
      ),
      enabled: !this.isOperatorAlreadyAppliedForColumn(
        tableName,
        columnOptionsWithType[0].name,
        defaultOperator
      ),
      isGlobal: false,
    };
    this.filterPanelMap.get(tableName).filters.set(this.nextFilterId, filter);
    this.filterPanelMap.get(tableName).latestFilterChangesAreApplied = false;
    localStorage.setItem(
      filterPanelMapLocalStorageKey,
      JSON.stringify(this.filterPanelMap)
    );
    this.nextFilterId = (parseInt(this.nextFilterId, 10) + 1).toString();
    localStorage.setItem(nextFilterIdLocalStorageKey, this.nextFilterId);
    this.resetCurrentPage(tableName);
    // if(filter.enabled) {
    //   this.fetchContentManagementTableData(tableName, () => this.updateMinRowsForTable(tableName));
    // }
  };

  @action
  removeFilter = (tableName, column, filterId) => {
    this.quickfiltersValues[`${tableName + column}`] = "";
    const fetchNeeded = this.filterPanelMap
      .get(tableName)
      .filters.get(filterId).enabled;
    this.filterPanelMap.get(tableName).filters.delete(filterId);
    localStorage.setItem(
      filterPanelMapLocalStorageKey,
      JSON.stringify(this.filterPanelMap)
    );
    this.resetCurrentPage(tableName);
    if (fetchNeeded) {
      this.fetchContentManagementTableData(tableName, () =>
        this.updateMinRowsForTable(tableName)
      );
    }
  };

  @action
  changeFilterColumn = (tableName, filterId, column) => {
    let filter = this.filterPanelMap.get(tableName).filters.get(filterId);
    let columnOptionWithType = filter.columnOptionsWithType.filter(
      (columnOption) => columnOption.name === column
    )[0];
    const operator =
      filter.type !== columnOptionWithType.type
        ? this.getDefaultOperatorForType(columnOptionWithType.type).value
        : filter.operator;
    // const previousState = filter.enabled;
    let updatedFilter = {
      ...filter,
      column: column,
      operator: operator,
      operatorOptions:
        filter.type !== columnOptionWithType.type
          ? this.getOperatorOptionsForType(columnOptionWithType.type)
          : filter.operatorOptions,
      type:
        filter.type !== columnOptionWithType.type
          ? columnOptionWithType.type
          : filter.type,
      value:
        filter.type !== columnOptionWithType.type
          ? this.getDefaultValueForType(columnOptionWithType.type)
          : filter.value,
      originalValue:
        filter.type !== columnOptionWithType.type
          ? this.getDefaultValueForType(columnOptionWithType.type)
          : filter.originalValue,
      enabled: !this.isOperatorAlreadyAppliedForColumn(
        tableName,
        column,
        operator
      ),
    };

    this.filterPanelMap.get(tableName).filters.set(filterId, updatedFilter);
    this.filterPanelMap.get(tableName).latestFilterChangesAreApplied = false;
    localStorage.setItem(
      filterPanelMapLocalStorageKey,
      JSON.stringify(this.filterPanelMap)
    );
    // if(updatedFilter.enabled || previousState !== updatedFilter.enabled) {
    //   this.fetchContentManagementTableData(tableName, () => this.updateMinRowsForTable(tableName));
    // }
  };

  @action
  changeFilterValue = (tableName, filterId, value) => {
    if (Array.isArray(value) && value.length === 0) {
      return;
    }
    let filter = this.filterPanelMap.get(tableName).filters.get(filterId);
    // const previousState = filter.enabled;
    let updatedFilter = {
      ...filter,
      value: this.getFilterValueInProperFormat(
        filter.type,
        filter.operator,
        value,
        false
      ),
      originalValue: this.getFilterValueInProperFormat(
        filter.type,
        filter.operator,
        value,
        true
      ),
    };
    this.filterPanelMap.get(tableName).filters.set(filterId, updatedFilter);
    this.filterPanelMap.get(tableName).latestFilterChangesAreApplied = false;
    localStorage.setItem(
      filterPanelMapLocalStorageKey,
      JSON.stringify(this.filterPanelMap)
    );
    // if(updatedFilter.enabled || previousState !== updatedFilter.enabled) {
    //   this.fetchContentManagementTableData(tableName, () => this.updateMinRowsForTable(tableName));
    // }
  };

  getFilterValueInProperFormat = (
    filterType,
    filterOperator,
    value,
    keepOriginal
  ) => {
    switch (filterType) {
      case "DATE":
        return moment(value).format("YYYY-MM-DD").toString();
      case "BOOLEAN":
        const booleanFilterValues = value.map((booleanValue) => {
          return booleanValue === "true" || booleanValue === true
            ? true
            : booleanValue === "false" || booleanValue === false
            ? false
            : null;
        });
        return booleanFilterValues;
      case "BRANCHTYPE":
        return value;
      case "PROJECTTYPE":
        return value;
      case "FRAMEWORKSTATUS":
        return value;
      case "APPROVALSTATUS":
        return value;
      default:
        if (filterOperator === "in") {
          if (value instanceof Array) {
            return value;
          } else {
            return keepOriginal
              ? [value]
              : value
                  .toString()
                  .split(";")
                  .map((v) => v.trim());
          }
        } else {
          if (value instanceof Array) {
            return value[0];
          } else {
            return value;
          }
        }
    }
  };

  @action
  changeFilterOperator = (tableName, filterId, operator) => {
    let filter = this.filterPanelMap.get(tableName).filters.get(filterId);
    // const previousState = filter.enabled;
    let updatedFilter = {
      ...filter,
      operator: operator,
      value: this.getFilterValueInProperFormat(
        filter.type,
        operator,
        filter.value,
        false
      ),
      originalValue: this.getFilterValueInProperFormat(
        filter.type,
        operator,
        filter.value,
        true
      ),
      enabled: !this.isOperatorAlreadyAppliedForColumn(
        tableName,
        filter.column,
        operator
      ),
    };
    this.filterPanelMap.get(tableName).filters.set(filterId, updatedFilter);
    this.filterPanelMap.get(tableName).latestFilterChangesAreApplied = false;
    localStorage.setItem(
      filterPanelMapLocalStorageKey,
      JSON.stringify(this.filterPanelMap)
    );
    // if(updatedFilter.enabled || previousState !== updatedFilter.enabled) {
    //   this.fetchContentManagementTableData(tableName, () => this.updateMinRowsForTable(tableName));
    // }
  };

  @action
  changeFilterStatus = (tableName, filterId, status) => {
    let filter = this.filterPanelMap.get(tableName).filters.get(filterId);
    if (
      this.isOperatorAlreadyAppliedForColumn(
        tableName,
        filter.column,
        filter.operator
      ) &&
      status === true
    ) {
      toastService.func_notify(
        "Filter can not be enabled since another filter with the same operator is already applied for the given column.",
        "error"
      );
    } else {
      let updatedFilter = {
        ...filter,
        enabled: status,
      };
      this.filterPanelMap.get(tableName).filters.set(filterId, updatedFilter);
      localStorage.setItem(
        filterPanelMapLocalStorageKey,
        JSON.stringify(this.filterPanelMap)
      );
      this.resetCurrentPage(tableName);
      this.fetchContentManagementTableData(tableName, () =>
        this.updateMinRowsForTable(tableName)
      );
    }
  };

  @action
  fetchAllEntities = () => {
    this.http.service_fetch_all_entities().then(
      action((entities) => {
        this.allEntitiesArray = entities;
      })
    );
  };

  @action
  switchBranch = (selectedBranchObject, callback) => {
    if (selectedBranchObject && selectedBranchObject.branch) {
      const {
        branch: { tid, name, branchType },
      } = selectedBranchObject;
      this.selectedBranchObject = {
        bid: tid,
        masterBid: selectedBranchObject?.masterBranch?.tid,
        branchName: name,
        branchType: branchType,
        projectName: selectedBranchObject.project.name,
        projectType: selectedBranchObject.project.projectType,
      };

      this.getOpenedTables.forEach((table) => {
        if (table.includes("prefiltered by URL")) {
          const searchParamKey = this.getUrlSearchParamKey();
          const searchParamValue = this.getUrlSearchParamValue();
          this.fetchURLPrefilteredData(searchParamKey, searchParamValue);
        } else {
          this.fetchContentManagementTableData(
            table,
            () => this.updateMinRowsForTable(table),
            false
          );
        }
      });

      if (callback) {
        callback();
      }
    }
  };

  @action
  func_restoreSelectedBranchFromLocalStorageIfStillExists = (
    maintenanceAgencies,
    callback
  ) => {
    const selectedBranchLocalStorageItem =
      window.localStorage.getItem("cm-selected-branch");
    const selectedBranch =
      selectedBranchLocalStorageItem !== null
        ? JSON.parse(selectedBranchLocalStorageItem)
        : null;

    if (selectedBranch !== null && selectedBranch.branch.name !== "") {
      const stillExistingBranch = [];
      maintenanceAgencies.forEach((maItem, maIndex) => {
        maItem.projects.forEach((projectItem, projectIndex) => {
          projectItem.branches.forEach((branchItem, branchIndex) => {
            if (
              branchItem.project.status !== "CLOSED" &&
              branchItem.branch.status !== "INACTIVE" &&
              branchItem.branch.tid === selectedBranch.branch.tid
            ) {
              stillExistingBranch.push(branchItem);
            }
          });
        });
      });
      if (stillExistingBranch.length === 1) {
        this.selectedBranchObject = {
          bid: selectedBranch.branch.tid,
          masterBid: selectedBranch.masterBranch.tid,
          branchName: selectedBranch.branch.name,
          branchType: selectedBranch.branch.branchType,
          projectName: selectedBranch.project.name,
          projectType: selectedBranch.project.projectType,
        };
        this.exporter.setSelectedBranchId(selectedBranch.branch.tid);
        this.importer.setSelectedBranchId(selectedBranch.branch.tid);
      } else {
        this.selectedBranchObject = {
          bid: null,
          masterBid: null,
          branchName: "",
          branchType: "",
          projectName: "",
          projectType: null,
        };
        this.exporter.setSelectedBranchId(null);
        this.importer.setSelectedBranchId(null);
        if (callback) {
          callback();
        }
        window.localStorage.setItem(
          "cm-selected-branch",
          JSON.stringify(noBranchSelectedBranch)
        );
      }
    }
  };

  @action
  updateMinRowsForTable = (tableName, callback) => {
    const minRows =
      this.getTableData(tableName).data.results.length === 0
        ? 1
        : this.getTableData(tableName).data.results.length >
          this.getLimit(tableName)
        ? this.getLimit(tableName)
        : this.getTableData(tableName).data.results.length;
    let tableData = this.tableMap.get(tableName);
    tableData.minRows = minRows;
    this.tableMap.set(tableName, tableData);
    localStorage.setItem(
      tableMapLocalStorageKey,
      JSON.stringify(this.tableMap)
    );
    if (callback) {
      callback();
    }
  };

  @action
  closeAllTable = () => {
    const openedTables = this.getOpenedTables;
    openedTables.forEach((table) => {
      this.toggleTableVisibility(table);
    });
  };

  @action
  toggleTableVisibility = (tableName) => {
    let tableData = this.tableMap.get(tableName);
    tableData.isOpen = !tableData.isOpen;
    this.tableMap.set(tableName, tableData);
    localStorage.setItem(
      tableMapLocalStorageKey,
      JSON.stringify(this.tableMap)
    );
  };

  @action
  toggleColumnSelectorForTable = (tableName) => {
    let tableData = this.tableMap.get(tableName);
    tableData.isColumnSelectorOpen = !tableData.isColumnSelectorOpen;
    this.tableMap.set(tableName, tableData);
    localStorage.setItem(
      tableMapLocalStorageKey,
      JSON.stringify(this.tableMap)
    );
  };

  @action
  selectAllColumnsForTable = (tableName) => {
    let tableData = this.tableMap.get(tableName);
    tableData.selectedColumns = tableData.data.metadata.visualizationFields;
    this.tableMap.set(tableName, tableData);
    localStorage.setItem(
      tableMapLocalStorageKey,
      JSON.stringify(this.tableMap)
    );
  };

  @action
  unselectAllColumnsForTable = (tableName) => {
    let tableData = this.tableMap.get(tableName);
    tableData.selectedColumns = [];
    this.tableMap.set(tableName, tableData);
    localStorage.setItem(
      tableMapLocalStorageKey,
      JSON.stringify(this.tableMap)
    );
  };

  @action
  toggleColumn = (tableName, column, checked) => {
    let tableData = this.tableMap.get(tableName);
    let selectedColumns = tableData.selectedColumns;
    if (checked && !selectedColumns.includes(column)) {
      selectedColumns.push(column);
      tableData.selectedColumns = selectedColumns;
      this.tableMap.set(tableName, tableData);
      localStorage.setItem(
        tableMapLocalStorageKey,
        JSON.stringify(this.tableMap)
      );
    } else {
      if (!checked && selectedColumns.includes(column)) {
        let selectedColumnsAfterRemoval = selectedColumns.filter(
          (selectedColumn) => selectedColumn !== column
        );
        tableData.selectedColumns = selectedColumnsAfterRemoval;
        this.tableMap.set(tableName, tableData);
        localStorage.setItem(
          tableMapLocalStorageKey,
          JSON.stringify(this.tableMap)
        );
      }
    }
  };

  @action
  toggleAll = (tableName, areAllRowsSelected, selectedRows) => {
    const tableData = this.tableMap.get(tableName);
    tableData.areAllRowsSelected = areAllRowsSelected;
    tableData.selectedRows = observable.map(selectedRows);
    this.tableMap.set(tableName, tableData);
    localStorage.setItem(
      tableMapLocalStorageKey,
      JSON.stringify(this.tableMap)
    );
  };

  @action
  toggleSelection = (tableName, selectedRows) => {
    const tableData = this.tableMap.get(tableName);
    tableData.selectedRows = observable.map(selectedRows);
    this.tableMap.set(tableName, tableData);
    localStorage.setItem(
      tableMapLocalStorageKey,
      JSON.stringify(this.tableMap)
    );
  };

  @action
  toggleSelectionEveryFilteredEntity = (tableName) => {
    let tableData = this.tableMap.get(tableName);
    tableData.isEveryFilteredEntitySelected =
      !tableData.isEveryFilteredEntitySelected;
    this.tableMap.set(tableName, tableData);
    if (tableData.isEveryFilteredEntitySelected) {
      this.toggleAll(tableName, true, []);
    } else {
      this.toggleAll(tableName, false, []);
    }
  };

  @action
  openTablesWhereEntityCanBeOrphan = () => {
    this.closeAllTable();
    EntityTypesThatCanBeOrphan.forEach((type) => {
      this.toggleTableVisibility(type);
    });
  };

  isEveryFilteredEntitySelected = (tableName) => {
    return computed(
      () => this.tableMap.get(tableName).isEveryFilteredEntitySelected
    ).get();
  };

  getQuickFilter = (tableName, column, type) => {
    return computed(() => {
      const filters = Object.values(this.getFiltersByTableName(tableName));
      return filters.find(
        (f) =>
          f.column === column &&
          f.operator === this.getQuickFilterOperatorForType(type).value &&
          f.enabled === true
      );
    }).get();
  };

  getQuickFilterOperatorForType = (type) => {
    switch (type) {
      case FilterType.STRING:
        return StringOperator.CONTAINS;
      case FilterType.NUMBER:
        return NumberOperator.IN;
      case FilterType.DATE:
        return DateOperator.ON;
      case FilterType.BOOLEAN:
        return BooleanOperator.IN;
      case FilterType.BRANCHTYPE:
        return EnumOperator.IN;
      case FilterType.PROJECTTYPE:
        return EnumOperator.IN;
      case FilterType.FRAMEWORKSTATUS:
        return EnumOperator.IN;
      case FilterType.APPROVALSTATUS:
        return EnumOperator.IN;
      case FilterType.LOV:
        return EnumOperator.IN;
      default:
        return StringOperator.CONTAINS;
    }
  };

  getDefaultOperatorForType = (type) => {
    switch (type) {
      case FilterType.STRING:
        return StringOperator.IN;
      case FilterType.NUMBER:
        return NumberOperator.IN;
      case FilterType.DATE:
        return DateOperator.ON;
      case FilterType.BOOLEAN:
        return BooleanOperator.IN;
      case FilterType.BRANCHTYPE:
        return EnumOperator.IN;
      case FilterType.PROJECTTYPE:
        return EnumOperator.IN;
      case FilterType.FRAMEWORKSTATUS:
        return EnumOperator.IN;
      case FilterType.APPROVALSTATUS:
        return EnumOperator.IN;
      default:
        return StringOperator.IN;
    }
  };

  getDefaultValueForType = (type) => {
    switch (type) {
      case "STRING":
        return [""];
      case "NUMBER":
        return 0;
      case "DATE":
        return moment().format("YYYY-MM-DD").toString();
      case "BOOLEAN":
        return [true];
      case "BRANCHTYPE":
        return ["PRODUCTION"];
      case "PROJECTTYPE":
        return ["FRAMEWORK"];
      case "FRAMEWORKSTATUS":
        return ["PUBLISHED"];
      case "APPROVALSTATUS":
        return ["APPROVED"];
      case "LOV":
        return []
      default:
        return [""];
    }
  };

  isOperatorAlreadyAppliedForColumn = (tableName, columnName, operator) => {
    return computed(
      () =>
        this.getEnabledFiltersByTableName(tableName)
          .filter((filter, filterIndex) => filter.column === columnName)
          .filter((filter, filterIndex) => filter.operator === operator)
          .length === 1
    ).get();
  };

  getOperatorOptionsForType(type) {
    switch (type) {
      case FilterType.STRING:
        return StringOperator;
      case FilterType.NUMBER:
        return NumberOperator;
      case FilterType.DATE:
        return DateOperator;
      case FilterType.BOOLEAN:
        return BooleanOperator;
      case FilterType.BRANCHTYPE:
        return EnumOperator;
      case FilterType.PROJECTTYPE:
        return EnumOperator;
      case FilterType.FRAMEWORKSTATUS:
        return EnumOperator;
      case FilterType.APPROVALSTATUS:
        return EnumOperator;
      case FilterType.LOV:
        return EnumOperator;
      default:
        return StringOperator;
    }
  }

  @computed
  get getNextFilterId() {
    return this.nextFilterId;
  }

  getFilterPanelDataByTableName = (tableName) => {
    return computed(() => this.filterPanelMap.get(tableName)).get();
  };

  getFiltersByTableName = (tableName) => {
    return computed(() =>
      toJS(this.filterPanelMap.get(tableName).filters)
    ).get();
  };

  areLatestFilterChangesApplied = (tableName) => {
    return computed(
      () => this.filterPanelMap.get(tableName).latestFilterChangesAreApplied
    ).get();
  };

  getFiltersCountByTableName = (tableName) => {
    return computed(
      () => this.filterPanelMap.get(tableName).filters.size
    ).get();
  };

  getEnabledFiltersByTableName = (tableName) => {
    return computed(() =>
      this.filterPanelMap.get(tableName).filters.size > 0
        ? Array.from(
            this.filterPanelMap.get(tableName).filters.values()
          ).filter((f) => f.enabled === true)
        : []
    ).get();
  };

  getEnabledGlobalFiltersByTableName = (tableName) => {
    return computed(() =>
      this.filterPanelMap.get(tableName).globalFiltersForTable &&
      Object.keys(this.filterPanelMap.get(tableName).globalFiltersForTable)
        .length > 0
        ? Array.from(
            Object.entries(
              this.filterPanelMap.get(tableName).globalFiltersForTable
            )
          ).filter((f) => f[1] === true)
        : []
    ).get();
  };

  isFilterPanelOpenForTable = (tableName) => {
    return computed(() => this.filterPanelMap.get(tableName).isOpen).get();
  };

  @computed
  get isGlobalFiltersPanelOpen() {
    return this.globalFiltersPanelOpen;
  }

  @computed
  get getShowProductionDataOnlyGlobally() {
    return this.showProductionDataOnlyGlobally;
  }

  @computed
  get getShowOrphansOnlyGlobally() {
    return this.showOrphansOnlyGlobally;
  }

  @computed
  get getShowWorkingDataOnlyGlobally() {
    return this.showWorkingDataOnlyGlobally;
  }

  getMinRowsByTableName = (tableName) => {
    return computed(() => this.tableMap.get(tableName).minRows).get();
  };

  getSelectedColumnsForTable = (tableName) => {
    return computed(() => this.tableMap.get(tableName).selectedColumns).get();
  };

  @action
  fetchContentManagementTableData = (tableName, callback, debounced = true) => {
    let tableData = this.tableMap.get(tableName);
    let fetchParams = {
      tableName: tableName,
      pageSize: tableData.limit.limit,
      queryOffset: tableData.limit.offset,
    };
    let sort = null;
    if (tableData.sorted.length > 0) {
      const mappedSort = tableData.sorted.map((sort) => {
        return {
          column: sort.id,
          direction: sort.desc ? "DESC" : "ASC",
        };
      });
      sort = mappedSort;
    }

    fetchParams.filter = this.transformFiltersForRequestBody(
      tableName,
      sort || []
    );

    let bids = [];

    const globalFiltersForTable =
      this.filterPanelMap.get(tableName).globalFiltersForTable;

    if (
      IS_BIRD_APP ||
      !this.userStore.isUserDaOrPoOrMao ||
      !this.userStore.isUserSystemAdministrator
    ) {
      bids.push(1);
    } else {
      if (
        globalFiltersForTable &&
        globalFiltersForTable["production"] === true &&
        this.globalFilters["production"].enabled === true
      ) {
        bids.push(1);
      }

      if (
        globalFiltersForTable &&
        globalFiltersForTable["working"] === true &&
        this.globalFilters["working"].enabled === true
      ) {
        if (this.selectedBranchObject && this.selectedBranchObject.bid) {
          bids.push(this.selectedBranchObject.bid);
        } else {
          bids.push(0); // if no working branch is selected - let's use some non-existing branch and not use "bid: null" that would mean any bid
        }
      }

      if (
        globalFiltersForTable &&
        globalFiltersForTable["master"] === true &&
        this.globalFilters["master"].enabled === true
      ) {
        bids.push(this.selectedBranchObject.masterBid);
      } else {
        bids.push(0);
      }

      if (
        this.globalFilters["production"].enabled === false &&
        this.globalFilters["working"].enabled === false &&
        this.globalFilters["master"].enabled === false
      ) {
        bids.length = 0;
        bids.push(1);
        this.allCurrentbranches.forEach((it) => {
          bids.push(it.branch.tid);
          bids.push(it.masterBranch.tid);
        });
      }
    }

    fetchParams.filter.bid = bids.length > 0 ? { in: [...bids] } : null;

    this.saveFetchedDataIntoStore(tableName, tableData.data);
    this.fetchData(fetchParams, tableName, callback);
  };

  fetchAllBranchesData = () => {
    if (IS_BIRD_APP) return;

    this.http.fetch_dictionary_administrator_data().then(
      action((data) => {
        if (data && data.maintenanceAgencies) {
          this.obj_dictionaryAdministratorData = data;
          data.maintenanceAgencies.forEach((maItem, maIndex) => {
            maItem.projects.forEach((projectItem, projectIndex) => {
              projectItem.branches.forEach((branchItem, branchIndex) => {
                if (
                  branchItem.project.status !== "CLOSED" &&
                  branchItem.branch.status !== "INACTIVE"
                ) {
                  this.allCurrentbranches.push(branchItem);
                }
              });
            });
          });
        }
      })
    );
  };

  fetchData = (fetchParams, tableName, callback) => {
    validationService.setLastCmQueryByType(fetchParams.tableName, fetchParams.filter)

    this.http.fetchContentManagementTableData(fetchParams).then((data) => {
      if (data && data.results) {
        this.saveFetchedDataIntoStore(tableName, data);
        this.filterPanelMap.get(tableName).latestFilterChangesAreApplied = true;
        localStorage.setItem(
          filterPanelMapLocalStorageKey,
          JSON.stringify(this.filterPanelMap)
        );
        if (callback) {
          callback();
        }
      }
    });
  };

  transformFiltersForRequestBody = (type, sort) => {
    const enabledFilters = this.getEnabledFiltersByTableName(type);
    const transformedEnabledFiltersMap = new Map();
    enabledFilters.forEach((filter) => {
      const column = filter.column;
      const operator =
        filter.operator === "isNotEmpty" ? "isEmpty" : filter.operator;
      const value =
        filter.operator === "isEmpty"
          ? true
          : filter.operator === "isNotEmpty"
          ? false
          : filter.value;

      if (transformedEnabledFiltersMap.has(column)) {
        const currentOperatorsWithValuesMapForColumn =
          transformedEnabledFiltersMap.get(column);
        if (!currentOperatorsWithValuesMapForColumn.has(operator)) {
          currentOperatorsWithValuesMapForColumn.set(operator, value);
          transformedEnabledFiltersMap.set(
            column,
            currentOperatorsWithValuesMapForColumn
          );
        }
      } else {
        const newOperatorsWithValuesMapForColumn = new Map();
        newOperatorsWithValuesMapForColumn.set(operator, value);
        transformedEnabledFiltersMap.set(
          column,
          newOperatorsWithValuesMapForColumn
        );
      }
    });

    const enabledFiltersMapAsJsonObject = this.mapToJson(
      transformedEnabledFiltersMap
    );
    enabledFiltersMapAsJsonObject["sort"] = sort;
    enabledFiltersMapAsJsonObject["type"] = type;

    return enabledFiltersMapAsJsonObject;
  };

  mapToJson = (map) => {
    const jsonObject = Object.create(null);
    map.forEach((value, key) => {
      if (value instanceof Map) {
        jsonObject[key] = this.mapToJson(value);
      } else {
        jsonObject[key] = value;
      }
    });
    return jsonObject;
  };

  @action
  closePreviouslyPrefilteredTables = (prefilteredTablesToDelete) => {
    for (let i = 0; i < prefilteredTablesToDelete.length; i++) {
      let tableData = this.tableMap.get(prefilteredTablesToDelete[i]);
      tableData.isOpen = false;
      this.tableMap.set(prefilteredTablesToDelete[i], tableData);
    }
    localStorage.setItem(
      tableMapLocalStorageKey,
      JSON.stringify(this.tableMap)
    );
  };

  @action
  processURLSearchParams = () => {
    let framework = null;
    let cube = null;
    const searchParams = new URLSearchParams(window.location.search);
    const prefilteredFramework = searchParams.has("framework");
    const prefilteredCube = searchParams.has("cube");
    let prefilteredTablesToDelete = [];
    if (!prefilteredFramework && !prefilteredCube) {
      prefilteredTablesToDelete = [
        "Framework prefiltered by URL",
        "Cube prefiltered by URL",
        "CubeStructureItem prefiltered by URL",
      ];
      this.closePreviouslyPrefilteredTables(prefilteredTablesToDelete);
    } else {
      if (prefilteredFramework) {
        prefilteredTablesToDelete = ["CubeStructureItem prefiltered by URL"];
        this.closePreviouslyPrefilteredTables(prefilteredTablesToDelete);
        framework = searchParams.get("framework");
        this.urlSearchParams.set("framework", framework);
        this.fetchURLPrefilteredData("framework", framework);
      }
      if (prefilteredCube) {
        prefilteredTablesToDelete = ["Framework prefiltered by URL"];
        this.closePreviouslyPrefilteredTables(prefilteredTablesToDelete);
        cube = searchParams.get("cube");
        this.urlSearchParams.set("cube", cube);
        this.fetchURLPrefilteredData("cube", cube);
      }
    }
  };

  fetchURLPrefilteredData = (entity, identifier) => {
    if (entity === "framework") {
      const prefilteredFrameworkTableName = "Framework prefiltered by URL";
      const prefilteredCubeTableName = "Cube prefiltered by URL";

      let prefilteredFrameworkTableData = this.tableMap.get(
        prefilteredFrameworkTableName
      );
      let prefilteredCubeTableData = this.tableMap.get(
        prefilteredCubeTableName
      );

      const frameworkFilter = {
        type: "Framework",
        filter: {
          frameworkId: {
            in: [identifier],
          },
          type: "Framework",
        },
        limit: {
          limit: prefilteredFrameworkTableData.limit.limit,
          offset: prefilteredFrameworkTableData.limit.offset,
        },
      };

      if (prefilteredFrameworkTableData.sorted.length > 0) {
        const mappedSort = prefilteredFrameworkTableData.sorted.map((sort) => {
          return {
            column: sort.id,
            direction: sort.desc ? "DESC" : "ASC",
          };
        });
        frameworkFilter.filter.sort = mappedSort;
      }

      const cubeFilter = {
        type: "Cube",
        filter: {
          frameworkId: {
            in: [identifier],
          },
          type: "Cube",
        },
        limit: {
          limit: prefilteredCubeTableData.limit.limit,
          offset: prefilteredCubeTableData.limit.offset,
        },
      };

      if (prefilteredCubeTableData.sorted.length > 0) {
        const mappedSort = prefilteredCubeTableData.sorted.map((sort) => {
          return {
            column: sort.id,
            direction: sort.desc ? "DESC" : "ASC",
          };
        });
        cubeFilter.filter.sort = mappedSort;
      }

      this.http.service_fetch_filteredEntities(frameworkFilter, (response) => {
        if (response && response.results) {
          this.saveFetchedDataIntoStore(
            prefilteredFrameworkTableName,
            response
          );
        }
      });

      this.http.service_fetch_filteredEntities(cubeFilter, (response) => {
        if (response && response.results) {
          this.saveFetchedDataIntoStore(prefilteredCubeTableName, response);
        }
      });
    } else {
      const prefilteredCubeTableName = "Cube prefiltered by URL";
      const prefilteredCubeStructureItemTableName =
        "CubeStructureItem prefiltered by URL";
      const prefilteredCubeTableData = this.tableMap.get(
        prefilteredCubeTableName
      );

      const cubeFilter = {
        type: "Cube",
        filter: {
          code: {
            in: [identifier],
          },
          type: "Cube",
        },
        limit: {
          limit: prefilteredCubeTableData.limit.limit,
          offset: prefilteredCubeTableData.limit.offset,
        },
      };

      if (prefilteredCubeTableData.sorted.length > 0) {
        const mappedSort = prefilteredCubeTableData.sorted.map((sort) => {
          return {
            column: sort.id,
            direction: sort.desc ? "DESC" : "ASC",
          };
        });
        cubeFilter.filter.sort = mappedSort;
      }

      this.http.service_fetch_filteredEntities(cubeFilter, (response) => {
        if (response && response.results) {
          this.saveFetchedDataIntoStore(prefilteredCubeTableName, response);

          const cubeStructureIds = [
            ...new Set(response.results.map((cube) => cube.cubeStructureId)),
          ];
          const prefilteredCubeStructureItemTableData = this.tableMap.get(
            prefilteredCubeStructureItemTableName
          );
          const cubeStructureItemFilter = {
            type: "CubeStructureItem",
            filter: {
              cubeStructureId: {
                in: cubeStructureIds,
              },
              type: "CubeStructureItem",
            },
            limit: {
              limit: prefilteredCubeStructureItemTableData.limit.limit,
              offset: prefilteredCubeStructureItemTableData.limit.offset,
            },
          };

          if (prefilteredCubeStructureItemTableData.sorted.length > 0) {
            const mappedSort = prefilteredCubeStructureItemTableData.sorted.map(
              (sort) => {
                return {
                  column: sort.id,
                  direction: sort.desc ? "DESC" : "ASC",
                };
              }
            );
            cubeStructureItemFilter.filter.sort = mappedSort;
          }

          this.http.service_fetch_filteredEntities(
            cubeStructureItemFilter,
            (response) => {
              if (response && response.results) {
                this.saveFetchedDataIntoStore(
                  prefilteredCubeStructureItemTableName,
                  response
                );
              }
            }
          );
        }
      });
    }
  };

  @action
  saveFetchedDataIntoStore = (tableName, data) => {
    let tableData = this.tableMap.get(tableName);
    const tableRowsWithId = data.results.map((row, rowIndex) => {
      return {
        ...row,
        _id: row.tid,
      };
    });
    const tableReadyData = {
      ...data,
      results: tableRowsWithId,
    };
    const newTableData = {
      isOpen: true,
      isColumnSelectorOpen: tableData.isColumnSelectorOpen,
      selectedColumns:
        tableData.selectedColumns && tableData.selectedColumns.length > 0
          ? tableData.selectedColumns
          : data.metadata.visualizationFieldsShownByDefault,
      selectedRows: tableData.selectedRows || observable.map(),
      areAllRowsSelected: tableData.areAllRowsSelected || false,
      isEveryFilteredEntitySelected:
        tableData.isEveryFilteredEntitySelected || false,
      sorted: tableData.sorted,
      limit: tableData.limit,
      data: tableReadyData,
    };
    this.tableMap.set(tableName, newTableData);
    localStorage.setItem(
      tableMapLocalStorageKey,
      JSON.stringify(this.tableMap)
    );
  };

  @action
  changeTableSettings = (tableName, changedPropName, newValue) => {
    let tableData = this.tableMap.get(tableName);
    switch (changedPropName) {
      case "page":
        tableData.limit.offset = tableData.limit.limit * newValue;
        break;
      case "pagesize":
        tableData.limit.limit = newValue.pageSize;
        tableData.limit.offset = tableData.limit.limit * newValue.pageIndex;
        break;
      case "sorted":
        tableData.sorted = newValue;
        break;
      default:
        return;
    }
    this.tableMap.set(tableName, tableData);
    localStorage.setItem(
      tableMapLocalStorageKey,
      JSON.stringify(this.tableMap)
    );
  };

  updateEntity = (type, entity) => {
    if (IS_BIRD_APP) return;

    const requestData = {
      actionType: "UpdateEntity",
      entity: entity,
      targetBid: this.selectedBranchObject.bid,
    };
    this.http.func_sendGeneralMessage(requestData, () =>
      this.fetchContentManagementTableData(type)
    );
  };

  @action
  fetchRelatedEntities = (type, selectedEntitiesMap) => {
    const { bid } = this.selectedBranchObject;
    const typeAndTidOfSelectedEntities = Array.from(
      selectedEntitiesMap.keys()
    ).map((key) => {
      return {
        tid: key,
        type: type,
      };
    });

    let bids = [];

    const globalFiltersForTable =
      this.filterPanelMap.get(type).globalFiltersForTable;

    if (
      globalFiltersForTable &&
      globalFiltersForTable["production"] === true &&
      this.globalFilters["production"].enabled === true
    ) {
      bids.push(1);
    }

    if (
      globalFiltersForTable &&
      globalFiltersForTable["working"] === true &&
      this.globalFilters["working"].enabled === true
    ) {
      if (this.selectedBranchObject && this.selectedBranchObject.bid) {
        bids.push(this.selectedBranchObject.bid);
      } else {
        bids.push(0);
      }
    }

    const params = {
      bid,
      typeTids: typeAndTidOfSelectedEntities,
      type: type,
      entityFilter: {
        type: type,
        bid:
          bids && bids.length > 0
            ? {
                in: bids,
              }
            : null,
      },
    };
    return this.http.fetchRelatedEntities(params);
  };

  @action
  setRelatedAndAffectedEntities = (entities) => {
    if (entities && entities.relatedObjects && entities.affectedObjects) {
      this.relatedEntities = entities.relatedObjects;
      this.affectedEntities = entities.affectedObjects;
      this.relatedEntityTypeCount = entities.relatedObjectTypeCount;
      this.affectedEntityTypeCount = entities.affectedObjectTypeCount;
    }
  };

  @action
  deleteEntities = (
    type,
    selectedEntitiesMap,
    isEveryFilteredEntitySelected,
    relatedEntities
  ) => {
    if (IS_BIRD_APP) return;

    const { bid } = this.selectedBranchObject;

    const entitiesToBeDeleted = Array.from(selectedEntitiesMap.keys()).map(
      (key) => {
        return {
          tid: key,
          type: type,
        };
      }
    );

    if (relatedEntities && Array.isArray(relatedEntities)) {
      relatedEntities.forEach((entity) => {
        entitiesToBeDeleted.push({
          tid: entity.tid,
          type: entity.type,
        });
      });
    }

    const requestData = {
      actionType: "DeleteEntities",
      entities: isEveryFilteredEntitySelected ? null : entitiesToBeDeleted,
      filter: isEveryFilteredEntitySelected
        ? validationService.getLastCmQueryByType().get(type)
        : null,
      targetBid: bid,
    };
    this.http.func_sendGeneralMessage(requestData);
  };

  @action
  deleteFrameworkEntities = (
    type,
    selectedEntitiesMap,
    isEveryFilteredEntitySelected,
    includeCoreObjects
  ) => {
    if (IS_BIRD_APP) return;

    const { bid } = this.selectedBranchObject;

    const entitiesToBeDeleted = Array.from(selectedEntitiesMap.keys()).map(
      (key) => {
        return {
          tid: key,
          type: type,
        };
      }
    );

    const requestData = {
      actionType: "DeleteFrameworkEntities",
      entities: isEveryFilteredEntitySelected ? null : entitiesToBeDeleted,
      frameworkFilter: isEveryFilteredEntitySelected
        ? validationService.getLastCmQueryByType().get(type)
        : null,
      targetBid: bid,
      includeCoreObjects: includeCoreObjects,
    };
    this.resetSelectedRows(type);
    this.http.func_sendGeneralMessage(requestData);
  };

  @action
  toggleGlobalFilter = (key) => {
    this.globalFilters[key].enabled = !this.globalFilters[key].enabled;
    cm_CONSTANT_entity_tables.forEach((tableGroup, tableGroupIndex) => {
      tableGroup.items.forEach((table, tableIndex) => {
        if (
          !table.label.includes("MAINTENANCE_AGENCY") &&
          !table.label.includes("prefiltered") &&
          table.label !== "FRAMEWORK"
        ) {
          this.toggleGlobalFilterForTable(
            key,
            table.value,
            this.globalFilters[key].enabled
          );
        }
      });
    });
    localStorage.setItem(
      "cm-global-filters",
      JSON.stringify(this.globalFilters)
    );
    this.getOpenedTables.forEach((table) =>
      this.fetchContentManagementTableData(table, null, false)
    );
  };

  @action
  toggleGlobalFilterForTable = (key, tableName, value = undefined) => {
    const globalFiltersForTable = {
      ...this.filterPanelMap.get(tableName).globalFiltersForTable,
    };
    if (!globalFiltersForTable) return;
    globalFiltersForTable[key] =
      value !== undefined ? value : !globalFiltersForTable[key];
    this.filterPanelMap.set(tableName, {
      ...this.filterPanelMap.get(tableName),
      globalFiltersForTable,
    });
    // store updated map to local storage
    localStorage.setItem(
      filterPanelMapLocalStorageKey,
      JSON.stringify(this.filterPanelMap)
    );
    if (value === undefined) {
      this.fetchContentManagementTableData(tableName, null, false);
    }
  };

  getPageCount = (tableName) => {
    return computed(() =>
      this.tableMap.get(tableName).data.totalResults === undefined ||
      this.tableMap.get(tableName).data.totalResults === -1
        ? -1
        : Math.ceil(
            this.tableMap.get(tableName).data.totalResults /
              this.tableMap.get(tableName).limit.limit
          )
    ).get();
  };

  getRowCount = (tableName) => {
    return computed(() =>
      this.tableMap.get(tableName).data.totalResults === undefined ||
      this.tableMap.get(tableName).data.totalResults === -1
        ? "N/A"
        : this.tableMap.get(tableName).data.totalResults
    ).get();
  };

  getLimit = (tableName) => {
    return computed(() => this.tableMap.get(tableName).limit.limit).get();
  };

  getColumns = (tableName) => {
    return computed(() =>
      this.tableMap.get(tableName).data.metadata.visualizationFields
        ? this.tableMap
            .get(tableName)
            .data.metadata.visualizationFields.map((field, fieldIndex) => {
              return {
                name: field,
                label:
                  this.tableMap.get(tableName).data.metadata.labelsForFields[
                    field
                  ],
                type: this.getTypeOfColumn(tableName, field),
              };
            })
        : []
    ).get();
  };

  getTextColor = (tableName, rowInfo, column) => {
    let color = "inherit";
    const { creationFields } = this.tableMap.get(tableName).data.metadata;
    if (creationFields && creationFields.includes(column.id)) {
      color = "green";
    } else {
      if (
        rowInfo &&
        rowInfo.original.entityClassification &&
        rowInfo.original.entityClassification === "DELETION_FROM_M"
      ) {
        color = "darkorange";
      } else if (
        rowInfo &&
        rowInfo.original.entityClassification &&
        rowInfo.original.entityClassification === "DELETION_FROM_P"
      ) {
        color = "red";
      } else {
        if (
          rowInfo &&
          rowInfo.original.bid &&
          rowInfo.original.bid === this.selectedBranchObject.bid
        ) {
          color = "blue";
        }
      }
    }
    return color;
  };

  getColumnWidth = (tableName, column) => {
    let { results } = this.tableMap.get(tableName).data;

    const maxWidth = 400;
    const magicSpacing = 12;

    let max = 0;

    for (let i = 0; i < results.length; i++) {
      if (results[i] !== undefined && results[i][column] !== null) {
        if ((results[i][column] || "null").length > max) {
          max = (results[i][column] || "null").length;
        }
      }
    }
    const additionalWidth =
      tableName === "MaintenanceAgency" || tableName === "Framework" ? 30 : 0;

    return (
      Math.min(maxWidth, Math.max(max, column.length) * magicSpacing) +
      additionalWidth
    );
  };

  getTypeOfColumn = (tableName, column) => {
    const { typesForFields } = this.tableMap.get(tableName).data.metadata;
    if (!typesForFields) {
      return FilterType.STRING;
    }
    switch (typesForFields[column]) {
      case "String":
        return FilterType.STRING;
      case "Date":
        return FilterType.DATE;
      case "Long":
        return FilterType.NUMBER;
      case "Boolean":
        return FilterType.BOOLEAN;
      case "BranchType":
        return FilterType.BRANCHTYPE;
      case "ProjectType":
        return FilterType.PROJECTTYPE;
      case "FrameworkStatus":
        return FilterType.FRAMEWORKSTATUS;
      case "ApprovalStatus":
        return FilterType.APPROVALSTATUS;
      case "LOV":
        return FilterType.LOV;
      default:
        return FilterType.STRING;
    }
  };

  getTableData = (tableName) => {
    return computed(() => this.tableMap.get(tableName)).get();
  };

  getSelectedRowsOfTable = (tableName) => {
    return computed(() =>
      this.tableMap
        .get(tableName)
        .data.results.filter((entity) =>
          Array.from(this.tableMap.get(tableName).selectedRows.keys()).includes(
            entity.tid
          )
        )
    ).get();
  };

  isTableOpened = (tableName) => {
    return computed(() => this.tableMap.get(tableName).isOpen).get();
  };

  @computed
  get getOpenedTables() {
    return Array.from(this.tableMap.keys()).filter(
      (key) => this.tableMap.get(key).isOpen === true
    );
  }

  isColumnSelectorOpenForTable = (tableName) => {
    return computed(
      () => this.tableMap.get(tableName).isColumnSelectorOpen
    ).get();
  };

  @computed
  get get_arr_entities() {
    try {
      if (this.allEntitiesArray) {
        const allEntitiesSortedArray = this.allEntitiesArray.slice().sort();
        return allEntitiesSortedArray.map((entity) => ({
          label: entity.substring(3),
          value: entity.substring(3),
        }));
      }
      return [];
    } catch (error) {
      console.error(error.message);
    }
    return [];
  }

  @computed
  get cm_computed_o_selectedBranch() {
    return this.selectedBranchObject;
  }

  getUrlSearchParamKey = () => {
    return computed(() => this.urlSearchParams.keys().next().value).get();
  };

  getUrlSearchParamValue = () => {
    return computed(() => Array.from(this.urlSearchParams.values())[0]).get();
  };

  isMenuGroupExpanded = (group) => {
    return computed(() => {
      return this.menuGroupStates[group] === "open";
    }).get();
  };

  @computed
  get getRelatedEntityTypes() {
    return this.relatedEntities.length > 0
      ? this.relatedEntities
          .map((entity) => entity.type)
          .filter((value, index, self) => self.indexOf(value) === index)
      : this.relatedEntities;
  }

  @computed
  get getRelatedEntityTypeCount() {
    return Object.keys(this.relatedEntityTypeCount).length === 0
      ? "No related entities found"
      : this.relatedEntityTypeCount;
  }

  @computed
  get getAffectedEntityTypes() {
    return this.affectedEntities.length > 0
      ? this.affectedEntities
          .map((entity) => entity.type)
          .filter((value, index, self) => self.indexOf(value) === index)
      : this.affectedEntities;
  }

  @computed
  get getAffectedEntityTypeCount() {
    return Object.keys(this.affectedEntityTypeCount).length === 0
      ? "No affected entities found"
      : this.affectedEntityTypeCount;
  }

  @computed
  get getGlobalFilters() {
    return this.globalFilters;
  }

  @computed
  get get_obj_maintenance_agencies() {
    return this.ob_fetched_ma_data.maintenanceAgencies;
  }

  @computed
  get getFrameworkTidsWhereActorIsPoOfProjectRelatedToFramework() {
    const { maintenanceAgencies } = this.ob_fetched_po_data;
    if (maintenanceAgencies.length === 0) {
      return [];
    }
    let frameworkTids = [];
    for (let ma of maintenanceAgencies) {
      for (let project of ma.projects) {
        if (project.frameworks) {
          const frameworks = project.frameworks.filter(
            (it) => it.type === "Framework"
          );
          frameworks.length > 0 &&
            frameworkTids.push(frameworks.map((it) => it.tid));
        }
      }
    }
    return frameworkTids.flatMap((tid) => tid);
  }

  @computed
  get getQuickfiltersValues() {
    return this.quickfiltersValues;
  }

  getGlobalFiltersForTable = (tableName) => {
    return computed(
      () => this.filterPanelMap.get(tableName).globalFiltersForTable
    ).get();
  };

  
  getLovsForColumnOfTable = (tableName, columnName) => {
    return computed(
      () => this.tableMap.get(tableName).data.metadata.lovByFieldName[columnName] ?? []
    ).get();
  };

}
